import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Textarea,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import {
  IconAlert,
  IconCases,
  IconFilter,
  IconPlus,
  IconRefresh
} from '../../components/icons';
import {
  billedAsLabels,
  billedAsTone,
  caseStatusLabels,
  caseStatusTone,
  formatDate,
  inferBilledAs,
  issueTypeLabels,
  raisedByLabel,
  raisedByTone
} from '../../components/admin/labels';
import { fieldErrorsFrom } from '../../utils/formErrors';
import { useCouriers } from '../../utils/useCouriers';
import { UpgradeModal } from '../../components/merchant/PlanUpgradeGrid';
import { merchantRoleLabel } from '../../components/plans';

const statusOptions = [
  'submitted',
  'evidence_prepared',
  'merchant_reviewed',
  'merchant_responded',
  'closed',
  'no_merchant_response'
];

const issueTypeOptions = [
  'not_arrived',
  'delivered_not_received',
  'damaged',
  'missing_items',
  'unsafe_place'
];

const emptyFilters = { status: '', issueType: '', page: 1, limit: 20 };

const emptyCase = {
  customerName: '',
  customerEmail: '',
  issueType: 'not_arrived',
  trackingNumber: '',
  courier: '',
  orderNumber: '',
  postcode: '',
  comments: ''
};

const MerchantCases = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const couriers = useCouriers();
  const [searchParams] = useSearchParams();

  const [cases, setCases] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyCase);
  const [createErrors, setCreateErrors] = useState({});
  // Set when creating a case was refused for want of a plan, so the error can
  // offer the plan chooser instead of just stating the problem.
  const [createBlockCode, setCreateBlockCode] = useState(null);
  const [creating, setCreating] = useState(false);
  // Known ahead of clicking "New case" (rather than only discovered from a
  // failed submit) so the button can say what is actually going to happen.
  const [blockedReason, setBlockedReason] = useState(null);
  const [merchant, setMerchant] = useState(null);
  // The plan chooser here is always the general one — a case-specific
  // "pay for this case or upgrade" belongs to that case, and now lives on its
  // own page (MerchantCaseDetail) where the block is shown.
  const [showUpgrade, setShowUpgrade] = useState(false);
  // True for exactly one render: the moment we land back here from Stripe
  // Checkout. Blocks the normal page render — the webhook that actually
  // applies the payment can lag by a few seconds, or (a local dev environment
  // with no `stripe listen` forwarding running) never arrive at all — until
  // confirmPortalCheckout has actually run. Case-scoped payments return to
  // the case page instead; what still lands here is a plan checkout.
  const [confirmingPayment, setConfirmingPayment] = useState(
    () => new URLSearchParams(window.location.search).get('payment') === 'success'
  );

  const fetchMerchant = useCallback(() => {
    return api
      .get('/api/portal/me')
      .then(({ data }) => {
        setMerchant(data.merchant);
        const pilotUsage = data.usage?.type === 'pilot' ? data.usage : null;
        if (pilotUsage?.ended) {
          setBlockedReason('Your pilot has ended.');
        } else if (data.merchant?.activationStatus === 'limited') {
          setBlockedReason('Your account is limited — settle any outstanding invoice.');
        } else {
          setBlockedReason(null);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchMerchant();
  }, [fetchMerchant]);

  // See confirmingPayment above for why this can't just wait on the webhook.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment !== 'success' && payment !== 'cancelled') return;

    const sessionId = params.get('session_id');
    params.delete('payment');
    params.delete('session_id');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));

    if (payment === 'cancelled') {
      toast('Payment was cancelled.', { tone: 'info' });
      return;
    }
    if (!sessionId) {
      setConfirmingPayment(false);
      return;
    }

    api
      .post('/api/portal/confirm-checkout', { sessionId })
      .then(({ data }) => {
        toast(data?.activated ? 'Payment confirmed.' : 'Payment received.', { tone: 'success' });
      })
      .catch((err) => {
        console.error('Confirm checkout error:', err);
        toast('Payment received, but we could not confirm it just now — refresh in a moment.', {
          tone: 'error'
        });
      })
      .finally(() => {
        setConfirmingPayment(false);
        fetchMerchant();
        fetchCases();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.issueType) params.append('issueType', filters.issueType);
      params.append('page', filters.page);
      params.append('limit', filters.limit);

      const { data } = await api.get(`/api/portal/cases?${params}`);
      setCases(data.cases || []);
      setMeta({ total: data.total || 0, totalPages: data.totalPages || 1 });
    } catch (error) {
      toast('Could not load your cases.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const openCase = useCallback(
    (caseId) => navigate(`/merchant/cases/${encodeURIComponent(caseId)}`),
    [navigate]
  );

  const openUpgrade = () => setShowUpgrade(true);

  // A case used to open as a modal on this page, addressed as ?case=<id>.
  // Links from that era — the dashboard's "Recent cases", a bookmark, an old
  // Stripe return URL — are forwarded to the case's own page rather than
  // silently landing on the unfiltered list.
  useEffect(() => {
    const legacyCaseId = searchParams.get('case');
    if (legacyCaseId) navigate(`/merchant/cases/${encodeURIComponent(legacyCaseId)}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    const found = {};
    if (!form.customerName.trim()) found.customerName = 'Enter the customer name.';
    if (!form.customerEmail.trim()) found.customerEmail = 'Enter the customer email.';
    else if (!/^\S+@\S+\.\S+$/.test(form.customerEmail.trim())) {
      found.customerEmail = 'Enter a valid email address.';
    }
    if (!form.trackingNumber.trim()) found.trackingNumber = 'Enter the tracking number.';
    if (!form.courier) found.courier = 'Select a courier.';
    if (!form.orderNumber.trim()) found.orderNumber = 'Enter the order number.';
    if (Object.keys(found).length) {
      setCreateErrors(found);
      return;
    }
    setCreateErrors({});
    setCreateBlockCode(null);

    setCreating(true);
    try {
      const { data } = await api.post('/api/portal/cases', {
        customer: { name: form.customerName.trim(), email: form.customerEmail.trim() },
        issueType: form.issueType,
        deliveryDetails: {
          trackingNumber: form.trackingNumber.trim(),
          courier: form.courier,
          orderNumber: form.orderNumber.trim(),
          postcode: form.postcode.trim() || undefined
        },
        evidence: { comments: form.comments.trim() || undefined }
      });
      setShowCreate(false);
      setForm(emptyCase);
      // Pay-per-case: this case is due for payment the moment it's created —
      // go straight to Stripe rather than leaving it sitting unpaid in the list.
      if (data?.checkoutUrl) {
        toast('Case created — redirecting to payment for pay-per-case.', { tone: 'success' });
        window.location.href = data.checkoutUrl;
        return;
      }
      toast('Case created.', { tone: 'success' });
      fetchCases();
      // Straight to the new case's page. Beyond being the natural next step,
      // subscription merchants have no single auto-charge the way pay-per-case
      // does — so if creating this case used up the plan's allowance, landing
      // on it surfaces the "pay for this case or upgrade" choice now, rather
      // than leaving the merchant to discover the block when they later try
      // to act on it.
      if (data?.caseId) {
        openCase(data.caseId);
      }
    } catch (error) {
      // These are the blocks a plan actually clears — LIMITED needs an
      // invoice paid instead, which the portal has no flow for.
      setCreateBlockCode(
        ['AWAITING_ACTIVATION', 'PILOT_ENDED', 'NO_PLAN'].includes(error.response?.data?.code)
          ? error.response.data.code
          : null
      );
      // Server paths are nested, so map them back onto this form's flat fields.
      setCreateErrors(
        fieldErrorsFrom(error, {
          fieldMap: {
            'customer.name': 'customerName',
            'customer.email': 'customerEmail',
            'deliveryDetails.trackingNumber': 'trackingNumber',
            'deliveryDetails.courier': 'courier',
            'deliveryDetails.orderNumber': 'orderNumber',
            'deliveryDetails.postcode': 'postcode'
          },
          fallback: 'Could not create this case.'
        })
      );
    } finally {
      setCreating(false);
    }
  };

  const activeFilters = ['status', 'issueType'].filter((k) => filters[k]).length;
  const createBlockedOnPlan = Boolean(createBlockCode);

  if (confirmingPayment) {
    return (
      <Card>
        <CardBody>
          <p className="py-10 text-center text-sm text-ink-500">Confirming your payment…</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {blockedReason && (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-warning-50 px-4 py-3.5 ring-1 ring-inset ring-warning-100">
          <div className="flex items-start gap-3">
            <IconAlert size={18} className="mt-0.5 shrink-0 text-warning-600" />
            <p className="text-sm text-warning-700">
              <span className="font-600">{blockedReason}</span> Upgrade to keep adding cases and
              taking action on the ones you have.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => openUpgrade()}>
            Upgrade
          </Button>
        </div>
      )}

      <Card>
        <CardHeader
          title="Filters"
          description={activeFilters ? `${activeFilters} applied` : 'Showing all cases'}
          actions={
            <>
              <Button variant="ghost" size="sm" onClick={fetchCases}>
                <IconRefresh size={15} />
                Refresh
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFilters(emptyFilters)}
                disabled={!activeFilters}
              >
                <IconFilter size={15} />
                Clear
              </Button>
              {blockedReason ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openUpgrade()}
                  title={blockedReason}
                >
                  <IconPlus size={15} />
                  Upgrade to add a case
                </Button>
              ) : (
                <Button size="sm" onClick={() => setShowCreate(true)}>
                  <IconPlus size={15} />
                  New case
                </Button>
              )}
            </>
          }
        />
        <CardBody>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Status" htmlFor="f-status">
              <Select
                id="f-status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              >
                <option value="">All statuses</option>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {caseStatusLabels[s]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Issue type" htmlFor="f-issue">
              <Select
                id="f-issue"
                value={filters.issueType}
                onChange={(e) => setFilters({ ...filters, issueType: e.target.value, page: 1 })}
              >
                <option value="">All issue types</option>
                {issueTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {issueTypeLabels[t]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Cases"
          description={loading ? 'Loading…' : `${meta.total} case${meta.total === 1 ? '' : 's'}`}
        />

        {loading ? (
          <TableSkeleton rows={6} cols={5} />
        ) : cases.length === 0 ? (
          <EmptyState
            icon={IconCases}
            title={activeFilters ? 'No cases match these filters' : 'No cases yet'}
            description={
              activeFilters
                ? 'Try clearing a filter to widen the search.'
                : 'Delivery issues raised against your store appear here.'
            }
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Case</Th>
                <Th>Customer</Th>
                <Th>Raised by</Th>
                <Th>Issue</Th>
                <Th>Courier</Th>
                <Th>Billed as</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <Tr key={c._id}>
                  <Td>
                    <button
                      type="button"
                      onClick={() => openCase(c.caseId)}
                      className="rounded font-600 text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                    >
                      {c.caseId}
                    </button>
                  </Td>
                  <Td>
                    <div className="font-medium text-ink-900">{c.customer?.name}</div>
                    <div className="text-xs text-ink-500">{c.customer?.email}</div>
                  </Td>
                  <Td>
                    <StatusPill tone={raisedByTone(c)}>{raisedByLabel(c)}</StatusPill>
                    {c.createdBy === 'merchant' && c.createdByUser?.merchantRole && (
                      <div className="mt-1 text-xs text-ink-500">
                        {merchantRoleLabel(c.createdByUser.merchantRole)}
                      </div>
                    )}
                  </Td>
                  <Td className="max-w-[13rem]">
                    <span className="block truncate">
                      {issueTypeLabels[c.issueType] || c.issueType}
                    </span>
                  </Td>
                  <Td>{c.deliveryDetails?.courier || '—'}</Td>
                  <Td>
                    {(() => {
                      const billedAs = inferBilledAs(c);
                      if (billedAs) {
                        return (
                          <StatusPill tone={billedAsTone[billedAs] || 'neutral'}>
                            {billedAsLabels[billedAs] || billedAs}
                          </StatusPill>
                        );
                      }
                      return c.billingCountedAt ? (
                        <span className="text-ink-500">Billed (plan unknown)</span>
                      ) : (
                        <span className="text-ink-500">Not yet</span>
                      );
                    })()}
                  </Td>
                  <Td>
                    <StatusPill tone={caseStatusTone[c.status] || 'neutral'}>
                      {caseStatusLabels[c.status] || c.status}
                    </StatusPill>
                  </Td>
                  <Td className="whitespace-nowrap text-ink-500">{formatDate(c.submittedAt)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        )}

        {!loading && meta.totalPages > 1 && (
          <div className="flex items-center justify-between gap-4 border-t border-ink-200 px-5 py-3.5">
            <p className="text-xs text-ink-500">
              Page {filters.page} of {meta.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={filters.page <= 1}
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={filters.page >= meta.totalPages}
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* New case */}
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setCreateErrors({});
          setCreateBlockCode(null);
        }}
        title="New case"
        description="Raise a delivery issue on a customer's behalf."
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={creating}>
              Create case
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {createErrors._general && (
            <div
              role="alert"
              className="rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-700 ring-1 ring-inset ring-danger-100"
            >
              {createErrors._general}
              {/* "Choose a plan" with nothing to choose it from is a dead end:
                  when that is precisely what's blocking the case, the plan
                  chooser has to be one click away rather than somewhere the
                  merchant has to go hunting for. */}
              {createBlockedOnPlan && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    setShowCreate(false);
                    openUpgrade();
                  }}
                >
                  Choose a plan
                </Button>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Customer name" htmlFor="c-name" required error={createErrors.customerName}>
              <Input
                id="c-name"
                value={form.customerName}
                onChange={(e) => setForm({ ...form, customerName: e.target.value })}
              />
            </Field>
            <Field label="Customer email" htmlFor="c-email" required error={createErrors.customerEmail}>
              <Input
                id="c-email"
                type="email"
                value={form.customerEmail}
                onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
              />
            </Field>
            <Field label="Issue type" htmlFor="c-issue" className="sm:col-span-2">
              <Select
                id="c-issue"
                value={form.issueType}
                onChange={(e) => setForm({ ...form, issueType: e.target.value })}
              >
                {issueTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {issueTypeLabels[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Tracking number"
              htmlFor="c-track"
              required
              error={createErrors.trackingNumber}
            >
              <Input
                id="c-track"
                value={form.trackingNumber}
                onChange={(e) => setForm({ ...form, trackingNumber: e.target.value })}
              />
            </Field>
            <Field label="Courier" htmlFor="c-courier" required error={createErrors.courier}>
              <Select
                id="c-courier"
                value={form.courier}
                onChange={(e) => setForm({ ...form, courier: e.target.value })}
              >
                <option value="">Select courier</option>
                {couriers.map((c) => (
                  <option key={c._id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Order number" htmlFor="c-order" required error={createErrors.orderNumber}>
              <Input
                id="c-order"
                value={form.orderNumber}
                onChange={(e) => setForm({ ...form, orderNumber: e.target.value })}
              />
            </Field>
            <Field label="Postcode" htmlFor="c-post" hint="Optional">
              <Input
                id="c-post"
                value={form.postcode}
                onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                placeholder="SW1A 1AA"
              />
            </Field>
            <Field label="Notes" htmlFor="c-notes" className="sm:col-span-2">
              <Textarea
                id="c-notes"
                rows={3}
                value={form.comments}
                onChange={(e) => setForm({ ...form, comments: e.target.value })}
                placeholder="What the customer reported."
              />
            </Field>
          </div>
        </div>
      </Modal>

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} merchant={merchant} />
    </div>
  );
};

export default MerchantCases;
